import {
  addToast,
  ErrorToast,
  SuccessToast,
  Toastß,
} from '@otter-ai/den-components';
import {
  AccountThirdPartyServiceOperationCancelledEvent,
  AccountThirdPartyServiceOperationFailEvent,
  AccountThirdPartyServiceOperationSuccessEvent,
  getLogger,
  getMeter,
  Screen,
  ThirdPartyService,
  ThirdPartyServiceCategory,
  ThirdPartyServiceOperation,
  trackEvent,
} from '@otter-ai/telemetry';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import {
  APPS_SETTINGS_OAUTH_REDIRECT_SOURCE_PARAM,
  AppsSettingsOauthRedirectSource,
  DROPBOX_APPS_SETTINGS_OAUTH_REDIRECT_PATH,
  SALESFORCE_APPS_SETTINGS_OAUTH_REDIRECT_PATH,
  SLACK_APPS_SETTINGS_OAUTH_REDIRECT_PATH,
  SLACK_WORKSPACE_APPS_SETTINGS_OAUTH_REDIRECT_PATH,
  ZOOM_APPS_SETTINGS_OAUTH_REDIRECT_PATH,
} from '@/constants/auth';
import type { TranslateFn } from '@/shared/types/common';
import { triggerDesktopReturnDeeplink } from '@/shared/utils/deeplink';
import {
  type ConnectDropboxToOtterAccountVars,
  useDropboxOAuth,
} from '../hooks/useDropboxOAuth';
import {
  type ConnectSalesforceToOtterAccountVars,
  useSalesforceOAuth,
} from '../hooks/useSalesforceOAuth';
import { hubspotRedirectUriFull } from '../utils/hubspot';
import {
  type ConnectSlackToOtterAccountVars,
  type ConnectSlackWorkspaceToOtterAccountVars,
  useSlackOAuth,
} from './slack/useSlackOAuth';
import {
  type ConnectHubspotToOtterAccountVars,
  useHubspotOAuth,
} from './useHubspotOAuth';
import {
  AlreadyAssociatedWithAnotherOtterAccountError,
  type ConnectZoomToOtterAccountVars,
  useZoomOAuth,
} from './zoom/useZoomOAuth';

const logger = getLogger('account-settings.integrations');
const meter = getMeter('account-settings.integrations');
const returningServicesCounters = meter.createCounter<{
  source: AppsSettingsOauthRedirectSource | string;
  sourceValue?: string;
}>('returning-services');
const successfulServicesCounters = meter.createCounter<{
  source: AppsSettingsOauthRedirectSource;
}>('successful-services');
const failedServicesCounters = meter.createCounter<{
  source: AppsSettingsOauthRedirectSource;
}>('failed-services');

/**
 * This hook is used to handle the OAuth return for Zoom and Dropbox to
 * the integrations settings page: `/setting/connected_apps`.
 * Add it at the top of the page on the client side.
 */
export function useAppsSettingsHandleOAuthReturn(): void {
  const t = useTranslations('settings:integrations');
  const { connectZoomToOtterAccountAsync } = useZoomOAuth();
  const { connectDropboxToOtterAccountAsync } = useDropboxOAuth();
  const { connectSalesforceToOtterAccount } = useSalesforceOAuth();
  const { connectHubspotToOtterAccount } = useHubspotOAuth();
  const {
    connectSlackToOtterAccountAsync,
    connectSlackWorkspaceToOtterAccountAsync,
  } = useSlackOAuth();

  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    const source = currentUrl.searchParams.get(
      APPS_SETTINGS_OAUTH_REDIRECT_SOURCE_PARAM,
    );

    if (!source) {
      return;
    }
    // TODO(sleahy): delete the state and code params
    currentUrl.searchParams.delete(APPS_SETTINGS_OAUTH_REDIRECT_SOURCE_PARAM);
    history.replaceState(null, '', currentUrl.toString());

    returningServicesCounters.add(1, {
      source: source || '(blank)',
    });

    switch (source) {
      case AppsSettingsOauthRedirectSource.DROPBOX:
        completeDropboxOAuthFlow(connectDropboxToOtterAccountAsync, t);
        return;
      case AppsSettingsOauthRedirectSource.GMAIL:
        return;
      case AppsSettingsOauthRedirectSource.GOOGLE:
        return;
      case AppsSettingsOauthRedirectSource.GOOGLE_DOCS:
        return;
      case AppsSettingsOauthRedirectSource.HUBSPOT:
        completeHubspotOAuthFlow(connectHubspotToOtterAccount, t);
        return;
      case AppsSettingsOauthRedirectSource.JIRA:
        return;
      case AppsSettingsOauthRedirectSource.MEMBER_SALESFORCE:
        completeSingleSalesforceOAuthFlow(connectSalesforceToOtterAccount, t);
        return;
      case AppsSettingsOauthRedirectSource.NOTION:
        return;
      case AppsSettingsOauthRedirectSource.GREENHOUSE:
        return;
      case AppsSettingsOauthRedirectSource.SALESFORCE:
        completeAllSalesforceOAuthFlow(connectSalesforceToOtterAccount, t);
        return;
      case AppsSettingsOauthRedirectSource.SLACK:
        completeSlackOAuthFlow(connectSlackToOtterAccountAsync, t);
        return;
      case AppsSettingsOauthRedirectSource.SLACK_WORKSPACE:
        completeSlackWorkspaceOAuthFlow(
          connectSlackWorkspaceToOtterAccountAsync,
          t,
        );
        return;
      case AppsSettingsOauthRedirectSource.ZOOM:
        completeZoomOAuthFlow(connectZoomToOtterAccountAsync, t);
        return;
      default:
        if (
          !Object.values(AppsSettingsOauthRedirectSource).includes(
            source as AppsSettingsOauthRedirectSource,
          )
        ) {
          addToast(<Toastß description={t('unsupportedSource', { source })} />);
        }
    }
  }, [
    connectZoomToOtterAccountAsync,
    connectDropboxToOtterAccountAsync,
    connectSalesforceToOtterAccount,
    connectHubspotToOtterAccount,
    connectSlackToOtterAccountAsync,
    connectSlackWorkspaceToOtterAccountAsync,
    t,
  ]);
}

async function completeZoomOAuthFlow(
  connectZoom: (vars: ConnectZoomToOtterAccountVars) => Promise<void>,
  t: TranslateFn,
) {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');

  if (!code) {
    logger.error({
      eventName: 'ConnectZoomAccountFailed',
      errorType: 'MissingCodeError',
      errorMessage: 'Missing OAuth code parameter',
    });
    failedServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.ZOOM,
    });
    addToast(<Toastß description={t('missingCode', { appName: 'Zoom' })} />);
    return;
  }

  const utmSource = getZoomUtmSourceFromUrlParams(url);

  const redirectUrl = new URL(
    ZOOM_APPS_SETTINGS_OAUTH_REDIRECT_PATH,
    location.origin,
  );

  try {
    await connectZoom({
      code,
      redirectUrl: redirectUrl.toString(),
    });

    trackEvent(
      AccountThirdPartyServiceOperationSuccessEvent({
        ThirdPartyService: ThirdPartyService.zoom,
        ThirdPartyServiceCategory: ThirdPartyServiceCategory.videoMeeting,
        ThirdPartyServiceOperation: ThirdPartyServiceOperation.connect,
        ZoomConnectUtmSource: utmSource,
      }),
    );

    addToast(
      <SuccessToast description={t('connectSuccess', { appName: 'Zoom' })} />,
    );
    successfulServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.ZOOM,
    });
    triggerDesktopReturnDeeplink();
  } catch (err) {
    if (err instanceof AlreadyAssociatedWithAnotherOtterAccountError) {
      logger.error({
        eventName: 'ConnectZoomAccountFailed',
        error: err,
      });
      addToast(
        <ErrorToast
          description={t('alreadyAssociatedWithAnotherOtterAccount', {
            appName: 'Zoom',
          })}
        />,
      );
      return;
    }
    logger.error({
      eventName: 'ConnectZoomAccountFailed',
      error: err,
    });
    addToast(
      <ErrorToast description={t('connectFailed', { appName: 'Zoom' })} />,
    );
    failedServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.ZOOM,
    });
  }

  cleanUrlOfConnectionParams();
}

function getZoomUtmSourceFromUrlParams(url: URL): string {
  const zoomState = url.searchParams.get('state');
  const decodedValue = zoomState ? atob(zoomState) : '';
  const utmSource = decodedValue ? decodedValue.split('utm_source=')[1] : '';
  return utmSource;
}

async function completeDropboxOAuthFlow(
  connectDropbox: (vars: ConnectDropboxToOtterAccountVars) => Promise<void>,
  t: TranslateFn,
) {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');

  if (!code) {
    logger.error({
      eventName: 'ConnectDropboxAccountFailed',
      errorMessage: 'Missing OAuth code parameter',
      errorType: 'MissingCodeError',
    });
    failedServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.DROPBOX,
    });
    addToast(<Toastß description={t('missingCode', { appName: 'Dropbox' })} />);
    return;
  }

  const redirectUrl = new URL(
    DROPBOX_APPS_SETTINGS_OAUTH_REDIRECT_PATH,
    location.origin,
  );

  try {
    await connectDropbox({
      code,
      redirectUrl: redirectUrl.toString(),
    });
    addToast(
      <SuccessToast
        description={t('connectSuccess', { appName: 'Dropbox' })}
      />,
    );
    successfulServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.DROPBOX,
    });
    triggerDesktopReturnDeeplink();
  } catch (err) {
    logger.error({
      eventName: 'ConnectDropboxAccountFailed',
      error: err,
    });
    addToast(
      <ErrorToast description={t('connectFailed', { appName: 'Dropbox' })} />,
    );
    failedServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.DROPBOX,
    });
  }

  // TODO(OTWEB-9138): Create a trackEvent for when the user connects their Dropbox account to Otter.

  cleanUrlOfConnectionParams();
}

async function completeSingleSalesforceOAuthFlow(
  connectSalesforce: (
    args: ConnectSalesforceToOtterAccountVars,
  ) => Promise<void>,
  t: TranslateFn,
) {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');

  if (!code) {
    logger.error({
      eventName: 'ConnectSalesforceAccountFailed',
      errorMessage: 'Missing OAuth code parameter',
      errorType: 'MissingCodeError',
      attributes: {
        IsMemberLink: true,
      },
    });
    failedServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.MEMBER_SALESFORCE,
    });
    addToast(
      <Toastß description={t('missingCode', { appName: 'Salesforce' })} />,
    );
    return;
  }

  try {
    await connectSalesforce({
      code,
      redirectUrl: salesforceRedirectUrl(true),
      instanceUrl: document.referrer,
      isMemberLink: true,
      useExistingLink: false,
    });
    successfulServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.MEMBER_SALESFORCE,
    });
    triggerDesktopReturnDeeplink();
  } catch (err) {
    logger.error({
      eventName: 'ConnectSalesforceAccountFailed',
      error: err,
      attributes: {
        IsMemberLink: true,
      },
    });
    failedServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.MEMBER_SALESFORCE,
    });
    addToast(
      <ErrorToast
        description={t('connectFailed', { appName: 'Salesforce' })}
      />,
    );
  }
}

async function completeAllSalesforceOAuthFlow(
  connectSalesforce: (
    args: ConnectSalesforceToOtterAccountVars,
  ) => Promise<void>,
  t: TranslateFn,
) {
  try {
    await connectSalesforce({
      code: '',
      redirectUrl: salesforceRedirectUrl(false),
      instanceUrl: document.referrer,
      isMemberLink: false,
      useExistingLink: true,
    });
    successfulServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.SALESFORCE,
    });
    triggerDesktopReturnDeeplink();
  } catch (err) {
    logger.error({
      eventName: 'ConnectSalesforceAccountFailed',
      error: err,
      attributes: {
        IsMemberLink: false,
      },
    });
    failedServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.SALESFORCE,
    });
    addToast(
      <ErrorToast
        description={t('connectFailed', { appName: 'Salesforce' })}
      />,
    );
  }
}

async function completeHubspotOAuthFlow(
  connectHubspot: (args: ConnectHubspotToOtterAccountVars) => Promise<void>,
  t: TranslateFn,
) {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');

  if (!code) {
    logger.error({
      eventName: 'ConnectHubSpotAccountFailed',
      errorMessage: 'Missing OAuth code parameter',
      errorType: 'MissingCodeError',
    });
    addToast(<Toastß description={t('missingCode', { appName: 'HubSpot' })} />);
    failedServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.HUBSPOT,
    });
    return;
  }

  const isMemberLink = url.searchParams.get('memberlink') !== 'false';

  try {
    await connectHubspot({
      code,
      redirectUrl: hubspotRedirectUriFull(isMemberLink),
      instanceUrl: document.referrer,
      isMemberLink,
    });
    successfulServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.HUBSPOT,
    });
    triggerDesktopReturnDeeplink();
  } catch (err) {
    logger.error({
      eventName: 'ConnectHubSpotAccountFailed',
      error: err,
    });
    failedServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.HUBSPOT,
    });
    addToast(
      <ErrorToast description={t('connectFailed', { appName: 'HubSpot' })} />,
    );
  }
}

function salesforceRedirectUrl(memberlink: boolean): string {
  const redirectUrl = new URL(
    SALESFORCE_APPS_SETTINGS_OAUTH_REDIRECT_PATH,
    location.origin,
  );
  redirectUrl.searchParams.append(
    APPS_SETTINGS_OAUTH_REDIRECT_SOURCE_PARAM,
    memberlink
      ? AppsSettingsOauthRedirectSource.MEMBER_SALESFORCE
      : AppsSettingsOauthRedirectSource.SALESFORCE,
  );
  return redirectUrl.toString();
}

async function completeSlackOAuthFlow(
  connectSlack: (vars: ConnectSlackToOtterAccountVars) => Promise<unknown>,
  t: TranslateFn,
) {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');

  if (!code) {
    logger.error({
      eventName: 'ConnectSlackAccountFailed',
      errorMessage: 'Missing OAuth code parameter',
      errorType: 'MissingCodeError',
    });
    failedServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.SLACK,
    });
    addToast(<Toastß description={t('missingCode', { appName: 'Slack' })} />);
    return;
  }

  const redirectUrl = new URL(
    SLACK_APPS_SETTINGS_OAUTH_REDIRECT_PATH,
    location.origin,
  );

  try {
    await connectSlack({
      code,
      redirectUrl: redirectUrl.toString(),
    });

    // TODO: Add telemetry event tracking for Slack connection

    cleanUrlOfConnectionParams();
    successfulServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.SLACK,
    });
    triggerDesktopReturnDeeplink();
  } catch (err) {
    logger.error({
      eventName: 'ConnectSlackAccountFailed',
      error: err,
    });
    failedServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.SLACK,
    });
    addToast(
      <ErrorToast description={t('connectFailed', { appName: 'Slack' })} />,
    );
  }
}

async function completeSlackWorkspaceOAuthFlow(
  connectSlackWorkspace: (
    vars: ConnectSlackWorkspaceToOtterAccountVars,
  ) => Promise<unknown>,
  t: TranslateFn,
) {
  const url = new URL(window.location.href);
  const redirectUrl = new URL(
    url.searchParams.get('redirect_uri') ??
      SLACK_WORKSPACE_APPS_SETTINGS_OAUTH_REDIRECT_PATH,
    location.origin,
  );
  if (url.searchParams.has('error')) {
    const errorType = url.searchParams.get('error') || 'Unknown';
    const errorDescription = url.searchParams.get('error_description') || '';

    addToast(<ErrorToast description={errorDescription} />);

    if (errorType === 'access_denied') {
      logger.error({
        eventName: 'ConnectSlackWorkspaceFailed',
        errorMessage: errorDescription || 'User denied access',
        errorType: 'AccessDeniedError',
      });
      trackEvent(
        AccountThirdPartyServiceOperationCancelledEvent({
          Screen: Screen.welcome,
          ThirdPartyService: ThirdPartyService.slack,
          ThirdPartyServiceCategory:
            ThirdPartyServiceCategory.workspaceInstallation,
          ThirdPartyServiceOperation: ThirdPartyServiceOperation.connect,
        }),
      );
      return;
    }
    logger.error({
      eventName: 'ConnectSlackWorkspaceFailed',
      errorMessage: errorDescription || 'Slack workspace connection failed',
      errorType: errorType,
    });
    trackEvent(
      AccountThirdPartyServiceOperationFailEvent({
        Screen: Screen.welcome,
        ThirdPartyService: ThirdPartyService.slack,
        ThirdPartyServiceCategory:
          ThirdPartyServiceCategory.workspaceInstallation,
        ThirdPartyServiceOperation: ThirdPartyServiceOperation.connect,
      }),
    );
  }
  const code = url.searchParams.get('code');

  if (!code) {
    logger.error({
      eventName: 'ConnectSlackWorkspaceFailed',
      errorMessage: 'Missing OAuth code parameter',
      errorType: 'MissingCodeError',
    });
    addToast(
      <ErrorToast description={t('missingCode', { appName: 'Slack' })} />,
    );
    failedServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.SLACK_WORKSPACE,
    });
    return;
  }

  try {
    await connectSlackWorkspace({
      code,
      redirectUrl: redirectUrl.toString(),
    });

    cleanUrlOfConnectionParams();
    successfulServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.SLACK_WORKSPACE,
    });
    triggerDesktopReturnDeeplink();
  } catch (err) {
    logger.error({
      eventName: 'ConnectSlackWorkspaceFailed',
      error: err,
    });
    failedServicesCounters.add(1, {
      source: AppsSettingsOauthRedirectSource.SLACK_WORKSPACE,
    });
    addToast(
      <ErrorToast description={t('connectFailed', { appName: 'Slack' })} />,
    );
  }
}

/**
 * Cleans the URL of params returned from the OAuth provider.
 */
function cleanUrlOfConnectionParams() {
  const cleanUrl = new URL(window.location.href);
  cleanUrl.searchParams.delete('code');
  cleanUrl.searchParams.delete('scope');
  cleanUrl.searchParams.delete('source');
  cleanUrl.searchParams.delete('state');
  history.replaceState(null, '', cleanUrl.toString());
}
